/**
 * 잔고(포지션) 데이터를 관리하는 컴포저블
 * OrderBalanceTable.vue의 getPositionStocks API 호출 로직을 기반으로 함
 */

import type { PositionStockData } from '@template/api';
import { useOrderDataStore } from '@template/stores';
import { useAccountStore } from '@template/stores';
import { stockService } from '@/services/api';
import { ref, computed, watch } from 'vue';

export function usePositionData() {
  const orderDataStore = useOrderDataStore();
  const accountStore = useAccountStore();

  // 로컬 상태
  const loading = ref(false);
  const error = ref<string | null>(null);

  // 스토어에서 데이터 가져오기
  const positionStocks = computed(() => orderDataStore.positionStocks);
  const selectedAccount = computed(() => accountStore.selectedAccountNo);

  /**
   * 잔고 데이터를 API에서 가져오는 함수
   * OrderBalanceTable.vue의 loadStockPositionData 로직을 기반으로 함
   */
  const loadPositionData = async () => {
    if (!selectedAccount.value) {
      error.value = '계좌가 선택되지 않았습니다.';
      return;
    }

    try {
      loading.value = true;
      error.value = null;
      orderDataStore.setPositionStocksLoading(true);
      orderDataStore.setPositionStocksError(null);

      const request = {
        accountNo: selectedAccount.value,
        stockCd: '', // 전체 조회
        nextKey: '',
      };

      const response = await stockService.getAllPositionStocks(request);

      if (response.data?.symbols) {
        orderDataStore.setPositionStocks(response.data.symbols);
      } else {
        orderDataStore.setPositionStocks([]);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '잔고 데이터를 불러오는데 실패했습니다.';
      error.value = errorMessage;
      orderDataStore.setPositionStocksError(errorMessage);
      console.error('잔고 데이터 로드 실패:', err);
    } finally {
      loading.value = false;
      orderDataStore.setPositionStocksLoading(false);
    }
  };

  /**
   * 특정 종목의 잔고 데이터를 업데이트하는 함수
   * SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
   * @param stockCd - 종목코드
   * @param positionCd - 포지션 코드 (L/S)
   * @param updates - 업데이트할 데이터 (purchaseDateTime, positionNo 포함 가능)
   * @param purchaseDateTime - 매입일자 (선택사항, updates에 없을 경우 사용)
   * @param positionNo - 잔고번호 (선택사항, updates에 없을 경우 사용)
   */
  const updatePositionStock = (
    stockCd: string,
    positionCd: string,
    updates: Partial<PositionStockData>,
    purchaseDateTime?: string,
    positionNo?: string
  ) => {
    orderDataStore.updatePositionStock(stockCd, positionCd, updates, purchaseDateTime, positionNo);
  };

  /**
   * 새로운 잔고 데이터를 추가하는 함수
   * @param data - 추가할 잔고 데이터
   */
  const addPositionStock = (data: PositionStockData) => {
    orderDataStore.addPositionStock(data);
  };

  /**
   * 잔고 데이터를 제거하는 함수
   * SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
   * @param stockCd - 종목코드
   * @param positionCd - 포지션 코드 (L/S)
   * @param purchaseDateTime - 매입일자 (선택사항)
   * @param positionNo - 잔고번호 (선택사항)
   */
  const removePositionStock = (
    stockCd: string,
    positionCd: string,
    purchaseDateTime?: string,
    positionNo?: string
  ) => {
    orderDataStore.removePositionStock(stockCd, positionCd, purchaseDateTime, positionNo);
  };

  /**
   * 검색어에 따라 필터링된 잔고 데이터
   * @param searchKeyword - 검색 키워드
   */
  const getFilteredPositionStocks = (searchKeyword?: string) => {
    if (!searchKeyword) {
      return positionStocks.value;
    }

    return positionStocks.value.filter((item) =>
      item.stockCd.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  };

  /**
   * 에러 상태 초기화
   */
  const clearError = () => {
    error.value = null;
    orderDataStore.setPositionStocksError(null);
  };

  /**
   * 데이터 새로고침
   */
  const refreshData = () => {
    loadPositionData();
  };

  // WebSocket 이벤트 리스너 관리
  // 주의: 채널 구독/해제는 useWebSocketSubscription에서 관리합니다.
  // 여기서는 WebSocket 메시지를 받아서 처리하는 이벤트 리스너만 등록합니다.
  let cleanupEventListeners: (() => void) | null = null;

  /**
   * WebSocket 이벤트 리스너 등록
   * 주문/체결 데이터가 변경될 때 잔고 데이터를 실시간으로 업데이트
   */
  const setupWebSocketEventListeners = () => {
    // 이미 등록되어 있으면 스킵
    if (cleanupEventListeners) {
      return;
    }

    // 계좌 잔고 이벤트 구독
    const handleAccountBalance = (event: CustomEvent) => {
      const { data, accountNo } = event.detail;
      if (accountNo === selectedAccount.value) {
        // 장부수량 > 0: 잔고 수정 또는 생성
        // 장부수량 = 0: 잔고 삭제
        // SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
        if (data.accountBookQuantity > 0) {
          // 잔고 수정 또는 생성
          // purchaseDateTime과 positionNo를 함께 전달하여 정확한 잔고를 찾아 업데이트
          orderDataStore.updatePositionStock(
            data.stockCd,
            data.positionCd,
            data,
            data.purchaseDateTime,
            data.positionNo
          );
        } else {
          // 잔고 삭제
          // purchaseDateTime과 positionNo를 함께 전달하여 정확한 잔고를 찾아 삭제
          orderDataStore.removePositionStock(
            data.stockCd,
            data.positionCd,
            data.purchaseDateTime,
            data.positionNo
          );
        }
      }
    };

    // 주문 거부 이벤트 구독 (청산가능 수량 복구)
    const handleOrderRejected = (event: CustomEvent) => {
      const { data, accountNo } = event.detail;
      // console.log('[usePositionData] orderRejected 이벤트 수신:', {
      //   accountNo,
      //   selectedAccount: selectedAccount.value,
      //   data,
      // });
      if (accountNo === selectedAccount.value) {
        // 청산 주문인 경우 청산가능 수량 복구
        // 청산 판단: orderCd === 'CLOSE_OUT'
        const isCloseOutOrder = data.orderCd === 'CLOSE_OUT';

        if (isCloseOutOrder && data.purchaseDateTime && data.balanceNo) {
          // 청산가능 수량 복구: 청산가능 = 청산가능 + 거부수량
          const existingPosition = orderDataStore.positionStocks.find(
            (position) =>
              position.stockCd === data.stockCd &&
              position.positionCd === data.positionCd &&
              position.purchaseDateTime === data.purchaseDateTime &&
              position.positionNo === data.balanceNo
          );

          if (existingPosition) {
            const newLiquidationPossibleQuantity =
              existingPosition.liquidationPossibleQuantity + data.rejectQuantity;
            // console.log('[usePositionData] 청산가능 수량 복구:', {
            //   stockCd: data.stockCd,
            //   positionCd: data.positionCd,
            //   oldQuantity: existingPosition.liquidationPossibleQuantity,
            //   rejectQuantity: data.rejectQuantity,
            //   newQuantity: newLiquidationPossibleQuantity,
            // });
            // purchaseDateTime과 positionNo를 함께 전달하여 정확한 잔고를 찾아 업데이트
            orderDataStore.updatePositionStock(
              data.stockCd,
              data.positionCd,
              {
                liquidationPossibleQuantity: newLiquidationPossibleQuantity,
              },
              data.purchaseDateTime,
              data.balanceNo
            );
          }
        }
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('accountBalance', handleAccountBalance as EventListener);
    window.addEventListener('orderRejected', handleOrderRejected as EventListener);

    // 정리 함수 저장
    cleanupEventListeners = () => {
      window.removeEventListener('accountBalance', handleAccountBalance as EventListener);
      window.removeEventListener('orderRejected', handleOrderRejected as EventListener);
      cleanupEventListeners = null;
    };
  };

  /**
   * WebSocket 이벤트 리스너 해제
   */
  const cleanupWebSocketEventListeners = () => {
    if (cleanupEventListeners) {
      cleanupEventListeners();
    }
  };

  return {
    // 상태
    loading,
    error,
    selectedAccount,

    // 액션
    loadPositionData,
    updatePositionStock,
    addPositionStock,
    removePositionStock,
    getFilteredPositionStocks,
    clearError,
    refreshData,

    // WebSocket 이벤트 리스너 관련
    setupWebSocketEventListeners,
    cleanupWebSocketEventListeners,
  };
}
